import { deleteThingModel, addThingModel, ThingModelItem } from '@/api';
import useAuthButton from '@/hooks/useAuthButton';
import { httpRequest } from '@/utils/request';
import { ExportOutlined, ImportOutlined, PlusOutlined, ThunderboltOutlined, AlertOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, message, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import { get } from 'lodash';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import DataModal, { DataModalRef } from './DataModal';
import { DetailModal, DetailModalRef } from './DeatilModal';
import { TypeSpecMap } from './helper';
import { SolutionCenterModal, SolutionCenterModalRef } from './SolutionCenterModal';
import { ProductActions, ProductEvents, ProductInfo, ProductProperties, ProductThingModelType, TypeSpec } from './types';
import { getUserGetinfo } from '@/api/system/user';

const { Text, Link } = Typography;
const { useForm, useWatch } = Form;

const PROVIDER_ROLE_KEY = 'provider';

/** 标准物模型（对标2026-06-17 Excel 物理模型表 — 14个属性） */
const STANDARD_PROPERTIES: Array<{
  name: string;
  code: string;
  type: string;
  access_mode: string;
  specs: Record<string, any>;
  description?: string;
}> = [
  { name: '数据ID', code: 'fid', type: 'int', access_mode: 'R', description: '仅为观察数据用，无其他用途', specs: { min: '0', max: '999999', step: '1' } },
  { name: '设备经度', code: 'lng', type: 'float', access_mode: 'R', description: '经纬度在地图上标记显示位置，客户离开净水机经纬度可追溯大致位置，防止净水机丢失', specs: { min: '-180', max: '180', step: '0.000001', length: '9' } },
  { name: '设备纬度', code: 'lat', type: 'float', access_mode: 'R', description: '', specs: { min: '-90', max: '90', step: '0.000001', length: '8' } },
  { name: '流量', code: 'flow', type: 'float', access_mode: 'R', description: '微水流量测量，量程0.5-6L/min；长时间无流量可怀疑短接传感器', specs: { min: '0', max: '6', step: '0.01', unit: 'L/Min', unitName: '升/分钟' } },
  { name: '温度', code: 'temp', type: 'float', access_mode: 'R', description: '温度测量，量程0-50℃', specs: { min: '0', max: '50', step: '0.1', unit: '℃', unitName: '摄氏度' } },
  { name: 'TDS值', code: 'tds', type: 'int', access_mode: 'R', description: 'TDS值测量，量程0-500mg/L；长时间无TDS可怀疑短接传感器', specs: { min: '0', max: '500', step: '1', unit: 'PPM', unitName: 'PPM' } },
  { name: '电源电压', code: 'pvolt', type: 'float', access_mode: 'R', description: '暂不使用，仅预留', specs: { min: '0', max: '24', step: '0.01', unit: 'V', unitName: '伏特' } },
  { name: '4G卡唯一识别码', code: 'iccid', type: 'text', access_mode: 'R', description: '唯一身份标识，唯一，不可更改', specs: { length: '255' } },
  { name: '4G信号强度', code: 'csq', type: 'int', access_mode: 'R', description: '优秀27-31，良好21-26，可用15-20，差<15', specs: { min: '0', max: '31', step: '1' } },
  { name: '电磁阀控制', code: 'valve', type: 'bool', access_mode: 'RW', description: '本地服务商扫码激活→打开；流量/时长耗尽→关阀；断电→关阀；滤芯到期→关阀，更换后激活', specs: { '0': '关', '1': '开' } },
  { name: '超滤膜滤芯安装时间', code: 'UF_time', type: 'date', access_mode: 'RW', description: 'UF超滤膜，默认12个月。到期后关阀，更换滤芯后扫码激活', specs: {} },
  { name: 'CM压缩活性炭滤芯安装时间', code: 'CM_time', type: 'date', access_mode: 'RW', description: 'CM后置压缩活性炭，默认6个月。到期后关阀，更换滤芯后扫码激活', specs: {} },
  { name: '剩余天数', code: 'remain_days', type: 'date', access_mode: 'RW', description: '设备租赁剩余天数，Unix时间戳(秒)，用于倒计时计算', specs: {} },
];

/** 标准告警规则 */
const ALERT_RULES: Array<{
  name: string;
  code: string;
  alert_level: string;
  description: string;
  option: Record<string, string>;
}> = [
  // ── 传感器测量类 ──
  { name: '流量超限报警', code: 'flow', alert_level: '重要', description: '微水流量超过0.5L/min时触发报警', option: { value_type: 'original', decide_condition: '> 0.5' } },
  { name: '温度过高报警', code: 'temp', alert_level: '紧急', description: '水温超过80℃时触发高温报警', option: { value_type: 'original', decide_condition: '> 80' } },
  { name: 'TDS水质超标报警', code: 'tds', alert_level: '重要', description: 'TDS值超过500mg/L时触发水质超标报警', option: { value_type: 'original', decide_condition: '> 500' } },
  { name: '电源电压过低报警', code: 'pvolt', alert_level: '提示', description: '电源电压低于2.8V（暂不使用，仅预留）', option: { value_type: 'original', decide_condition: '< 2.8' } },
  // ── 信号强度 ──
  { name: '4G信号弱报警', code: 'csq', alert_level: '提示', description: '4G信号强度低于15，可能导致数据上报不稳定', option: { value_type: 'original', decide_condition: '< 15' } },
  // ── 控制类 ──
  { name: '电磁阀异常关闭报警', code: 'valve', alert_level: '紧急', description: '净水机断电后电磁阀异常关闭；或流量/时长耗尽后电磁阀断电', option: { decide_condition: '= false' } },
  // ── 超滤膜滤芯(UF_time) ──
  { name: '超滤膜(UF)-1月到期提醒', code: 'UF_time', alert_level: '提示', description: 'UF超滤膜滤芯距到期还有约1个月，请提前准备更换', option: { value_type: 'date_expiry', decide_condition: '> 28944000000' } },
  { name: '超滤膜(UF)-10天到期警告', code: 'UF_time', alert_level: '重要', description: 'UF超滤膜滤芯距到期还有约10天，即将到期', option: { value_type: 'date_expiry', decide_condition: '> 30662400000' } },
  { name: '超滤膜(UF)-3天到期紧急', code: 'UF_time', alert_level: '紧急', description: 'UF超滤膜滤芯仅剩约3天有效期！请立即更换', option: { value_type: 'date_expiry', decide_condition: '> 31276800000' } },
  { name: '超滤膜(UF)-到期自动关阀', code: 'UF_time', alert_level: '紧急', description: 'UF超滤膜滤芯已到期，系统自动关闭电磁阀，更换滤芯后扫码激活净水机', option: { value_type: 'date_expiry', decide_condition: '> 31536000000' } },
  // ── CM压缩活性炭滤芯(CM_time) ──
  { name: 'CM压缩活性炭-1月到期提醒', code: 'CM_time', alert_level: '提示', description: 'CM后置压缩活性炭滤芯距到期还有约1个月，请提前准备更换', option: { value_type: 'date_expiry', decide_condition: '> 12960000000' } },
  { name: 'CM压缩活性炭-10天到期警告', code: 'CM_time', alert_level: '重要', description: 'CM后置压缩活性炭滤芯距到期还有约10天，即将到期', option: { value_type: 'date_expiry', decide_condition: '> 14688000000' } },
  { name: 'CM压缩活性炭-3天到期紧急', code: 'CM_time', alert_level: '紧急', description: 'CM后置压缩活性炭滤芯仅剩约3天有效期！请立即更换', option: { value_type: 'date_expiry', decide_condition: '> 15292800000' } },
  { name: 'CM压缩活性炭-到期自动关阀', code: 'CM_time', alert_level: '紧急', description: 'CM后置压缩活性炭滤芯已到期，系统自动关闭电磁阀，更换滤芯后扫码激活净水机', option: { value_type: 'date_expiry', decide_condition: '> 15552000000' } },
  // ── 剩余天数(remain_days)12个月到期 ──
  { name: '租赁天数-1月到期提醒', code: 'remain_days', alert_level: '提示', description: '剩余天数距到期还有约1个月，请及时续费', option: { value_type: 'date_expiry', decide_condition: '> 28944000000' } },
  { name: '租赁天数-10天到期警告', code: 'remain_days', alert_level: '重要', description: '剩余天数距到期还有约10天，即将到期', option: { value_type: 'date_expiry', decide_condition: '> 30672000000' } },
  { name: '租赁天数-5天到期紧急', code: 'remain_days', alert_level: '紧急', description: '剩余天数仅剩约5天有效期！请立即续费', option: { value_type: 'date_expiry', decide_condition: '> 31104000000' } },
  { name: '租赁天数-到期自动关阀', code: 'remain_days', alert_level: '紧急', description: '剩余天数已到期，系统自动关闭电磁阀', option: { value_type: 'date_expiry', decide_condition: '> 31536000000' } },
];

const TypeMap: { [key: string]: string } = {
  action: '服务类型',
  event: '事件类型',
  property: '属性类型',
};

interface DefaultDetailProps {
  productInfo: ProductInfo | undefined;
  isCloudProduct: boolean;
  loadProductInfo: () => void;
  productId: string;
}

export const DefaultDetail: React.FC<DefaultDetailProps> = ({ productInfo, isCloudProduct, loadProductInfo, productId }) => {
  // _BUILD_TEST_MARKER_20260609_
  const [importing, setImporting] = useState(false);
  const [alertImporting, setAlertImporting] = useState(false);
  const [form] = useForm();
  const nameValue = useWatch('name', form);
  const typeValue = useWatch('type', form);
  const dataModalRef = useRef<DataModalRef>(null);
  const detailModalRef = useRef<DetailModalRef>(null);
  const solutionCenterModalRef = useRef<SolutionCenterModalRef>(null);
  const { checkPermissionDisabled } = useAuthButton();

  const handleImport = async () => {
    if (!productId) {
      message.error('产品ID不存在，无法导入标准物模型');
      return;
    }
    console.log('[一键导入] 开始导入标准物模型, productId:', productId, '共', STANDARD_PROPERTIES.length, '个属性');
    setImporting(true);

    try {
      let successCount = 0;
      let failCount = 0;

      for (const prop of STANDARD_PROPERTIES) {
        try {
          await addThingModel({
            product_id: productId,
            name: prop.name,
            code: prop.code,
            description: prop.description || '',
            thing_model_type: 'property',
            tag: '',
            property: {
              access_model: prop.access_mode,
              require: false,
              type: prop.type,
              specs: JSON.stringify(prop.specs),
            },
            sort: 1,
            storage_mode: 1,
            external: {},
          });
          successCount++;
          console.log('[一键导入] 属性导入成功:', prop.name, '(' + prop.code + ')');
        } catch (e) {
          console.warn('[一键导入] 属性导入失败:', prop.name, e);
          failCount++;
        }
      }

      if (successCount > 0 && failCount === 0) {
        message.success(`成功导入 ${successCount} 个标准物模型属性`);
      } else if (successCount > 0) {
        message.warning(`已导入 ${successCount} 个属性，${failCount} 个导入失败`);
      } else {
        message.error('所有物模型属性导入失败');
      }

      if (successCount > 0) {
        message.success(`成功导入 ${successCount} 个属性，即将刷新页面...`);
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (e) {
      console.error('[一键导入] 异常:', e);
      message.error('导入标准物 model异常，请重试');
    }

    setImporting(false);
  };

  const handleAlertImport = async () => {
    if (!productId) {
      message.error('产品ID不存在，无法导入告警规则');
      return;
    }
    console.log('[告警导入] 开始导入, productId:', productId, '共', ALERT_RULES.length, '条规则');
    setAlertImporting(true);

    try {
      // 第0步：刷新产品信息，确保拿到最新物模型数据
      loadProductInfo();
      await new Promise(resolve => setTimeout(resolve, 1500));

      // 第0.5步：API 直接查询物模型属性数量
      const propsCheckResp = await httpRequest({
        method: 'GET',
        url: `/api/v1/product/${productId}`,
      });
      const propsCheckData = propsCheckResp as any;
      const propertyCount = propsCheckData?.result?.properties?.length || 0;
      console.log('[告警导入] 物模型属性数量:', propertyCount);

      if (propertyCount === 0) {
        message.warning('该产品暂无物模型属性，请先点击"一键导入标准物模型"后再导入告警规则');
        setAlertImporting(false);
        return;
      }

      // 第一步：查询该产品下的所有设备ID
      const deviceResp = await httpRequest({
        method: 'GET',
        url: `/api/v1/devices?product_id=${productId}&pageSize=1000`,
      });
      const deviceData = deviceResp as any;
      const deviceIds: string[] = (deviceData?.result?.list || []).map((d: { id: string }) => d.id);

      if (deviceIds.length === 0) {
        message.warning('该产品下暂无设备，请先添加设备后再导入告警规则');
        setAlertImporting(false);
        return;
      }
      console.log('[告警导入] 找到设备:', deviceIds.length, '台', 'IDs:', JSON.stringify(deviceIds));

      // 第二步：查询所有已有规则（用于同名覆盖，分页查询确保不遗漏）
      const allExistingRules: Array<{ id: string; name: string }> = [];
      for (let page = 1; ; page++) {
        const searchResp = await httpRequest({
            method: 'GET',
            url: `/api/v1/alert-rule?page=${page}&pageSize=100&productId=${productId}`,
          });
        const searchData = searchResp as any;
        const list: Array<{ id: string; name: string }> = searchData?.result?.list || searchData?.result || [];
        if (list.length === 0) break;
        allExistingRules.push(...list);
        if (list.length < 100) break;
      }
      const existingMap = new Map(allExistingRules.map((r: { id: string; name: string }) => [r.name, r.id]));
      console.log('[告警导入] 已有规则:', allExistingRules.length, '条', '名称列表:', allExistingRules.map((r: { name: string }) => r.name));

      let createSuccessCount = 0;
      let configFailCount = 0;
      const configuredRuleIds: Array<{ id: string; name: string }> = [];

      // ════════════ 阶段一：创建+配置所有规则 ════════════
      console.log('[告警导入] ===== 阶段一：创建并配置所有规则 =====');
      for (const rule of ALERT_RULES) {
        try {
          let ruleId: string | undefined;

          // 同名覆盖：如果已存在同名规则，先删除再创建
          if (existingMap.has(rule.name)) {
            const oldRuleId = existingMap.get(rule.name)!;
            console.log('[告警导入] 发现同名规则，删除旧规则:', rule.name, 'oldId:', oldRuleId);
            try {
              await httpRequest({ method: 'DELETE', url: `/api/v1/alert-rule/${oldRuleId}` });
              console.log('[告警导入] 旧规则已删除:', rule.name);
            } catch (delErr) {
              console.warn('[告警导入] 删除旧规则失败(非致命):', rule.name, delErr);
            }
          }

          // Step A: 创建新规则骨架
          const createResp = await httpRequest({
            method: 'POST',
            url: '/api/v1/alert-rule',
            data: {
              name: rule.name,
              alert_type: '设备告警',
              alert_level: rule.alert_level,
              description: rule.description,
              product_id: productId,
            },
          });
          const createData = createResp as any;
          if (!(createData?.success === true || createData?.errorCode === 0)) {
            console.error('[告警导入] 创建失败:', rule.name, createData);
            configFailCount++;
            continue;
          }

          ruleId = createData?.result || createData?.id;
          if (!ruleId || typeof ruleId !== 'string' || (Array.isArray(ruleId) && ruleId.length === 0)) {
            const reSearchResp = await httpRequest({
              method: 'GET',
              url: `/api/v1/alert-rule?page=1&pageSize=5&productId=${productId}`,
            });
            const reSearchData = reSearchResp as any;
            const reList = reSearchData?.result?.list || [];
            const found = reList.find((r: { name: string; id: string }) => r.name === rule.name);
            if (found?.id) ruleId = found.id;
          }

          if (!ruleId || typeof ruleId !== 'string') {
            console.error('[告警导入] 无法获取ruleId:', rule.name);
            configFailCount++;
            continue;
          }

          // Step B: 配置子规则（触发条件、设备绑定）
          const updatePayload = {
            id: ruleId,
            condition: 'anyone',
            sub_rule: [{
              trigger: '设备数据触发',
              product_id: productId,
              device_ids: deviceIds,
              option: {
                code: rule.code,
                ...rule.option,
              },
            }],
            silence_time: 300,
            notify: [],
          };

          const updateResp = await httpRequest({
            method: 'PUT',
            url: `/api/v1/alert-rule/${encodeURIComponent(ruleId)}`,
            data: updatePayload,
          });

          const updateData = updateResp as any;
          if (updateData?.success === true || updateData?.errorCode === 0) {
            createSuccessCount++;
            configuredRuleIds.push({ id: ruleId, name: rule.name });
            console.log('[告警导入] 规则就绪:', rule.name, '→ ruleId:', ruleId);
          } else {
            console.error('[告警导入] 配置失败:', rule.name, updateData);
            try { await httpRequest({ method: 'DELETE', url: `/api/v1/alert-rule/${ruleId}` }); } catch (_) {}
            configFailCount++;
          }
        } catch (e) {
          console.error('[告警导入] 异常:', rule.name, e);
          configFailCount++;
        }
      }

      // ════════════ 阶段二：等待后端缓存刷新后批量启用 ════════════
      let startSuccessCount = 0;
      let startFailCount = 0;

      if (configuredRuleIds.length > 0) {
        console.log('[告警导入] ===== 等待5秒后批量启用', configuredRuleIds.length, '条规则 =====');
        await new Promise(resolve => setTimeout(resolve, 5000));
        loadProductInfo();

        for (const { id: ruleId, name: ruleName } of configuredRuleIds) {
          try {
            const startResp = await httpRequest({
              method: 'POST',
              url: `/api/v1/alert-rule/${encodeURIComponent(ruleId)}/start`,
            }) as any;
            if (startResp?.success === true || startResp?.errorCode === 0) {
              startSuccessCount++;
            } else {
              console.warn('[告警导入] 启用失败:', ruleName, startResp?.errorMsg);
              startFailCount++;
            }
          } catch (startErr) {
            console.warn('[告警导入] 启用异常:', ruleName, startErr);
            startFailCount++;
          }
        }
      }

      if (startSuccessCount === configuredRuleIds.length) {
        message.success(`成功导入并启用 ${startSuccessCount} 条告警规则`);
      } else if (createSuccessCount > 0) {
        message.warning(
          `已创建 ${createSuccessCount} 条规则，启用 ${startSuccessCount} 条` +
          (startFailCount > 0 ? `，${startFailCount} 条需手动启用` : ''),
        );
      } else {
        message.error('所有告警规则导入失败');
      }
    } catch (e) {
      console.error('[告警导入] 异常:', e);
      message.error('导入告警规则异常，请重试');
    }

    setAlertImporting(false);
  };

  const dataSource = useMemo(() => {
    if (!productInfo) return [];
    const list: Array<ProductThingModelType & { key?: string }> = [
      ...(productInfo.properties || []).map((item) => ({ ...item, type: 'property' as const })),
      ...(productInfo.events || []).map((item) => ({ ...item, type: 'event' as const })),
      ...(productInfo.actions || []).map((item) => ({ ...item, type: 'action' as const })),
    ];
    return list.filter(({ type, name }) => {
      return type.includes(typeValue || '') && name.includes(nameValue || '');
    });
  }, [productInfo, nameValue, typeValue]);

  const columns = [
    {
      title: '功能类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (value: string) => {
        return (
          <>
            {value === 'property' && <Tag color="green">{TypeMap[value]}</Tag>}
            {value === 'action' && <Tag color="blue">{TypeMap[value]}</Tag>}
            {value === 'event' && <Tag color="volcano">{TypeMap[value]}</Tag>}
          </>
        );
      },
    },
    {
      title: '功能名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '标识符',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: '数据类型',
      key: 'type_spec_type',
      render: (_: any, record: ProductThingModelType) => {
        const type = get(record, 'type_spec.type', '') as string;
        return TypeSpecMap[type as keyof typeof TypeSpecMap] || '-';
      },
    },
    {
      title: '读写类型',
      dataIndex: 'access_mode',
      key: 'access_mode',
      width: 140,
      render: (value: string) => {
        const map: { [key: string]: string } = {
          RW: '可下发可上报（RW）',
          R: '只上报（R）',
          W: '只下发（W）',
        };
        return map[value] || value || '-';
      },
    },
    {
      title: '数据定义',
      key: 'type_spec_specs',
      render: (_: any, record: ProductThingModelType) => {
        const type = get(record, 'type_spec.type', '') as string;
        const specsStr = get(record, 'type_spec.specs', '{}') as string;
        let specs: any;
        try {
          specs = typeof specsStr === 'string' ? JSON.parse(specsStr) : specsStr;
          // 容错双重序列化
          if (typeof specs === 'string') specs = JSON.parse(specs);
        } catch {
          return '-';
        }

        if (type === 'array') {
          return '数组个数: ' + specs?.size;
        } else if (type === 'text') {
          return '数据长度: ' + specs?.length;
        } else if (type === 'date') {
          return 'Unix时间戳 (秒), 用于倒计时计算';
        } else if (type === 'bool' || type === 'enum') {
          return Object.entries(specs)
            .map(([k, v]) => `${k}-${v}`)
            .map((text, index) => (
              <Tag color="processing" key={index}>
                {text}
              </Tag>
            ));
        } else if (type === 'int' || type === 'float') {
          return (
            <>
              <div>
                取值范围: {specs?.min}-{specs?.max}
              </div>
              <div>步长: {specs?.step}</div>
              <div>
                单位: {specs?.unit} ({specs?.unitName})
              </div>
            </>
          );
        }
        return '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 160,
      render: (_: any, record: ProductThingModelType) => (
        <Space split={<Text type="secondary">/</Text>}>
          <Link
            onClick={() => {
              detailModalRef.current?.open(record);
            }}
          >
            查看
          </Link>
          <Link
            onClick={() => {
              detailModalRef.current?.open(record, true);
            }}
          >
            编辑
          </Link>
          <Popconfirm
            title="此操作将永久删除该记录, 是否继续?"
            okText="删除"
            cancelText="取消"
            placement="topRight"
            disabled={checkPermissionDisabled('gateway:product:tsl:remove')}
            onConfirm={async () => {
              try {
                const res = await deleteThingModel({
                  thing_model_id: record.id,
                  thing_model_type: record.type,
                });
                if (res.success) {
                  message.success('删除成功');
                  loadProductInfo();
                } else {
                  message.error(res?.errorMsg);
                }
              } catch (e) {
                message.error('删除失败');
              }
            }}
          >
            <Link disabled={checkPermissionDisabled('gateway:product:tsl:remove')}>删除</Link>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card className="m20">
      <span style={{display:'none'}}>DEPLOY_TEST_V2_20260609_0130_OK</span>
      <div className="mb15" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Form layout="inline" form={form} initialValues={{ type: '', name: '' }} className="mb0">
          <Form.Item name="type" className="mb0" style={{ marginBottom: 0 }}>
            <Select
              placeholder="功能类型 (全部)"
              style={{ minWidth: 130 }}
              allowClear
              size="middle"
              options={[
                { label: '属性', value: 'property' },
                { label: '事件', value: 'event' },
                { label: '服务', value: 'action' },
              ]}
            />
          </Form.Item>
          <Form.Item name="name" className="mb0" style={{ marginBottom: 0 }}>
            <Input placeholder="请输入产品名称搜索" style={{ width: 180 }} allowClear size="middle" />
          </Form.Item>
        </Form>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <Button
            type="primary"
            size="middle"
            icon={<PlusOutlined />}
            onClick={() => detailModalRef.current?.open(undefined, true)}
            disabled={checkPermissionDisabled('gateway:product:tsl:add')}
          >
            添加
          </Button>
          <Button
            size="middle"
            icon={<ThunderboltOutlined />}
            onClick={handleImport}
            loading={importing}
            disabled={checkPermissionDisabled('gateway:product:tsl:add')}
          >
            一键导入标准物模型
          </Button>
          <Button
            size="middle"
            icon={<AlertOutlined />}
            onClick={handleAlertImport}
            loading={alertImporting}
            disabled={checkPermissionDisabled('gateway:product:tsl:add')}
          >
            导入告警规则
          </Button>
          <Button
            size="middle"
            icon={<ImportOutlined />}
            onClick={() => productId && dataModalRef.current?.open(productId, 'import')}
            disabled={checkPermissionDisabled('gateway:product:tsl:import')}
          >
            导入
          </Button>
          <Button
            size="middle"
            icon={<ExportOutlined />}
            onClick={() => productId && dataModalRef.current?.open(productId, 'export')}
            disabled={checkPermissionDisabled('gateway:product:tsl:export')}
          >
            导出
          </Button>
          <Button
            size="middle"
            onClick={() => solutionCenterModalRef.current?.open()}
          >
            方案中心
          </Button>
        </div>
      </div>
      <Table
        dataSource={dataSource}
        rowKey="id"
        columns={columns}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
        }}
        bordered
        size="small"
        scroll={{ x: 'max-content' }}
      />
      <DataModal
        ref={dataModalRef}
        onSuccess={() => {
          loadProductInfo();
        }}
      />
      <DetailModal
        ref={detailModalRef}
        productInfo={productInfo}
        onSuccess={() => {
          loadProductInfo();
        }}
      />
      <SolutionCenterModal
        ref={solutionCenterModalRef}
        copyToProductId={productId}
        onSelect={() => {
          loadProductInfo();
        }}
        onSuccess={() => {
          loadProductInfo();
        }}
      />
    </Card>
  );
};
